import React, { useEffect, useState } from "react";
import Layout from "../../layout/Main/Layout";
import { Button, Typography, Modal, Box, TextField, MenuItem, IconButton } from "@mui/material";
import CustomTable from "../../components/Custom/Table/CustomTable";
import { get, post } from "../../config/axios";
import { toastMessage } from "../../utils/toastMessage";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import Searchbar from "../../components/Custom/SearchBar/Searchbar";
import { useDebouncedValue } from "../../helper/debounce";

const Options = () => {
  const [questions, setQuestions] = useState([]);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [pageCount, setPageCount] = useState(1);
  const [totalData, setTotalData] = useState(0);
  const [perPage, setPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);

  const [formData, setFormData] = useState({
    name: "",
    optionType: "",
    options: [""],
  });

  const lastOptionRef = React.useRef(null);

  const tableColumns = [
    { id: "name", label: "Name", minWidth: 70, align: "left" }, 
    { id: "optionType", label: "Option Type", minWidth: 100, align: "center" },
    { id: "optionList", label: "Options", minWidth: 100, align: "center" },
]

  const debouncedSearch = useDebouncedValue(search, 500);

  const fetchQuestions = async (searchValue = "") => {
    try {
      setLoading(true);
      const res = await get(`/api/dashboard/cbt/options`);
      setQuestions(
        res?.questions?.map((item) => ({
          ...item,
          name: item.name|| "",
          optionType: item.optionType || "",
          optionList: item.options.map((opt) => opt.value).join(" , "),
        //   action: { edit: true, delete: true },
        })) || []
      );
      setPageCount(res?.totalPage || 1);
      setTotalData(res?.totalData || 0);
      setPerPage(res?.perPage || 10);
      setCurrentPage(res?.currentPage || 1);
    } catch (error) {
      console.error("Fetch error:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (search === "") {
      fetchQuestions();
    } else if (debouncedSearch) {
      fetchQuestions(debouncedSearch);
    }
  }, [search, debouncedSearch, page]);

  const handleOptionChange = (value, index) => {
    const updatedOptions = [...formData.options];
    updatedOptions[index] = value;
    setFormData({ ...formData, options: updatedOptions });
  };

  const addOptionField = () => {
    setFormData({ ...formData, options: [...formData.options, ""] });
    setTimeout(() => {
      lastOptionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 0);
    toastMessage("New option field added", "success");
  };

  const removeOptionField = (index) => {
    const updatedOptions = formData.options.filter((_, i) => i !== index);
    setFormData({ ...formData, options: updatedOptions });
    toastMessage("Option field removed", "success");
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
  
      // Filter out empty options
      const filteredOptions = formData.options
        .map((opt) => opt.trim())
        .filter((opt) => opt !== "");
  
      if (formData.optionType === "RATING" && filteredOptions.length === 0) {
        toastMessage("Please add at least one valid option for Rating type", "error");
        setLoading(false);
        return;
      }
  
      const payload = {
        name: formData.name.trim(),
        optionType: formData.optionType,
        options: filteredOptions.map((opt) => ({ value: opt })),
        asset: "https://applore-dev-projects-1.s3.ap-south-1.amazonaws.com/public/ck2eqck2eqmedical-care.svg",
      };
  
      await post("/api/dashboard/cbt/option", payload);
      toastMessage("Successfully added", "success");
      fetchQuestions();
      setModalOpen(false);
      setFormData({ name: "", optionType: "", options: [""] });
    } catch (error) {
      console.error("Submission error:", error);
      toastMessage("Error while processing the request", "error");
    } finally {
      setLoading(false);
    }
  };
  


  return (
    <Layout>
      <div style={{ padding: "1rem" }}>
      <Typography variant="h5" style={{ fontWeight: "bold", margin: "1.5rem" }}>
          CBT Options
        </Typography>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div style={{ width: "40%" }}>
            {/* <Searchbar
              search={(text) => {
                setSearch(text.trim());
                setPage(1);
              }}
              placeholder="Search by sub category name"
              debounceTime={1000}
            /> */}
          </div>
          <Button
            onClick={() => setModalOpen(true)}
            variant="outlined"
            startIcon={<AddIcon fontSize="large" />}
            style={{ fontWeight: "bold" }}
          >
            Add Options
          </Button>
        </div>
        <CustomTable
          data={questions}
          columns={tableColumns}
          handlePageChange={setPage}
          pageNumber={page}
          pageCount={pageCount}
          totalData={totalData}
          perPage={perPage}
          currentPage={currentPage}
          loading={loading}
        />
      </div>

      {/* Custom Modal */}
      <Modal open={modalOpen} onClose={() => setModalOpen(false)}>
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: 900,
          maxHeight: "80vh",
          bgcolor: "background.paper",
          boxShadow: 24,
          p: 0,
          borderRadius: 2,
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Box sx={{ p: 4, overflowY: "auto", flex: 1 }}>
          <Typography variant="h6">Add CBT Option</Typography>
          <TextField
            label="Name"
            value={formData.name}
            required
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            fullWidth
            sx={{ mb: 2 }}
          />
          <TextField
            select
            label="Option Type"
            value={formData.optionType}
            required
            onChange={(e) => {
              const value = e.target.value;
              if (value === "Y/N") {
                setFormData({ ...formData, optionType: value, options: ["Yes", "No"] });
              } else {
                setFormData({ ...formData, optionType: value, options: [""] });
              }
            }}
            fullWidth
            sx={{ mb: 2 }}
          >
            <MenuItem value="Y/N">Y/N</MenuItem>
            <MenuItem value="RATING">RATING</MenuItem>
          </TextField>

          {formData.options?.map((option, index) => (
            <Box key={index} display="flex" alignItems="center" gap={1} mb={1}
            ref={index === formData.options.length - 1 ? lastOptionRef : null}
        >
              {formData.optionType && (
                <TextField
                  label={`Option ${index + 1}`}
                  value={option}
                  onChange={(e) => handleOptionChange(e.target.value, index)}
                  fullWidth
                  disabled={formData.optionType === "Y/N"}
                />
              )}
              {formData.optionType === "RATING" && formData.options.length > 1 && (
                <IconButton onClick={() => removeOptionField(index)} color="error">
                  <DeleteIcon />
                </IconButton>
              )}
            </Box>
          ))}
        </Box>

        <Box
          sx={{
            p: 2,
            borderTop: "1px solid #ddd",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            bgcolor: "background.paper",
          }}
        >
          {formData.optionType === "RATING" && (
            <Button onClick={addOptionField} startIcon={<AddIcon />}>
              Add Option
            </Button>
          )}

          <Button
            variant="contained"
            color="primary"
            onClick={handleSubmit}
            disabled={!formData.name.trim() || !formData.optionType}
          >
            Submit
          </Button>
        </Box>
      </Box>
     </Modal>
    </Layout>
  );
};

export default Options;
